import { motion } from "framer-motion";
import { Megaphone, Users, HeartPulse, Home, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/constants/brand";

const PROGRAMS = [
  {
    icon: Megaphone,
    num: "01",
    title: "Outreach Programs",
    text: "Meeting fathers where they are with resources, events, and community connection that build belonging.",
  },
  {
    icon: Users,
    num: "02",
    title: "Youth Engagement & Mentorship",
    text: "Pairing experienced mentors with the next generation to model leadership, accountability, and care.",
  },
  {
    icon: HeartPulse,
    num: "03",
    title: "Wellness Initiatives",
    text: "Mental wellness, emotional support, and healing-centered spaces so fathers can thrive, not just survive.",
  },
  {
    icon: Home,
    num: "04",
    title: "Family Development",
    text: "Equipping dads to lead at home with love and intention, strengthening the whole family unit.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1 },
  }),
};

export const Programs = () => {
  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="py-24 md:py-32 bg-[#0f1216]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-3 text-[#e2562a] uppercase tracking-[0.35em] text-xs font-bold mb-6">
              <span className="h-px w-10 bg-[#e2562a]" />
              What We Do
            </span>
            <h2 className="font-display uppercase text-5xl md:text-6xl text-white tracking-tight leading-[0.95]">
              Building stronger fathers
            </h2>
          </div>
          <p className="text-white/55 md:max-w-sm leading-relaxed">
            Every program is designed to support the whole man: mind, heart, and
            home, so families and communities grow stronger together.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROGRAMS.map((p, i) => (
            <motion.div
              key={p.title}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              data-testid={`program-card-${i}`}
              className="group relative bg-[#16191e] border border-white/10 p-8 hover:border-[#e2562a] transition-colors duration-300 overflow-hidden"
            >
              <span className="absolute top-4 right-5 font-display text-5xl text-white/5 group-hover:text-[#e2562a]/20 transition-colors">
                {p.num}
              </span>
              <div className="relative">
                <div className="h-12 w-12 flex items-center justify-center bg-[#e2562a]/10 group-hover:bg-[#e2562a] transition-colors mb-6">
                  <p.icon className="h-6 w-6 text-[#e2562a] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display uppercase text-lg text-white tracking-wide mb-3 leading-tight">
                  {p.title}
                </h3>
                <p className="text-sm text-white/55 leading-relaxed">{p.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative h-72 md:h-96 overflow-hidden group border border-white/10"
          >
            <img
              src={IMAGES.community}
              alt="Men supporting one another in community"
              className="w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d10] via-[#0b0d10]/40 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <h3 className="font-display uppercase text-2xl text-white tracking-wide">
                Community & Belonging
              </h3>
              <p className="text-white/70 text-sm mt-1">
                For every father, of every background.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative h-72 md:h-96 overflow-hidden group border border-white/10"
          >
            <img
              src={IMAGES.mentorship}
              alt="Youth mentorship and engagement"
              className="w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d10] via-[#0b0d10]/40 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <h3 className="font-display uppercase text-2xl text-white tracking-wide">
                Mentorship That Lasts
              </h3>
              <p className="text-white/70 text-sm mt-1">
                Pouring into the next generation of leaders.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
